import type { RuleResponse, RuleArray } from '../types'
import { passwordValidation } from '../utils/passwordValidation'
import { useNuxtApp } from '#imports'

export const useCoreRules = () => {
    // Global composer instead of useI18n(): this composable is also called from the
    // `rules` Nuxt plugin, where useI18n() throws (no component setup context).
    const { t } = useNuxtApp().$i18n

    const required = (message: string = t('core.validationRules.required.default')) => {
        return (value?: string | number | boolean): RuleResponse => {
            // Whitespace-only strings count as empty — backends trim before validating.
            if (typeof value === 'string' ? value.trim() : value) return true

            return message
        }
    }

    const minLength = (minVal: number, message: string) => {
        return (value?: string): RuleResponse => {
            if (!value) return true

            return value.length >= minVal || message
        }
    }

    /**
     * Validate a string is an email.
     *
     * Regex is sourced from this SO discussion: https://stackoverflow.com/a/46181
     *
     * @param value An email
     * @returns true or a string
     */
    function email(value?: string): RuleResponse {
        if (!value) return true

        return !!value.match(/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/) || t('core.validationRules.email.invalid')
    }

    function password(value?: string): RuleResponse {
        if (!value) return true

        const validationObject = passwordValidation(value)

        return validationObject.isValid || t('core.validationRules.password.invalid')
    }

    function passwordOld(value?: string): RuleResponse {
        if (!value) return true

        if (value.length < 8) return t('core.validationRules.password.tooShort')

        return true
    }

    const between = (minVal: number, maxVal: number, message: string) => {
        return (value?: string): RuleResponse => {
            if (!value) return true

            return (value.length >= minVal && value.length <= maxVal) || message
        }
    }

    const mustDiffer = (current: () => string | undefined, message: string) => {
        return (value?: string): RuleResponse => {
            const currentValue = current()
            if (!value || !currentValue) return true
            return value !== currentValue || message
        }
    }

    const noNumbers = (value: string): RuleResponse => !value.match(/\d+/g) || t('core.validationRules.noNumbers')

    // Allows unicode letters (including æøå, accented chars), whitespace, hyphens and apostrophes
    const noSpecialCharacters = (value: string): RuleResponse => !!value.match(/^[\p{L}\s'-]*$/u) || t('core.validationRules.noSpecialChars')

    const EMAIL: RuleArray = [
        required(t('core.validationRules.required.email')),
        email,
    ]

    const NEW_PASSWORD: RuleArray = [
        required(t('core.validationRules.required.newPassword')),
        password,
    ]

    const EXISTING_PASSWORD: RuleArray = [
        required(t('core.validationRules.required.password')),
        passwordOld,
    ]

    return {
        required,
        minLength,
        email,
        password,
        passwordOld,
        between,
        mustDiffer,
        noNumbers,
        noSpecialCharacters,
        EMAIL,
        EXISTING_PASSWORD,
        NEW_PASSWORD,
    }
}
